// Copyright (C) 2025, Lifefinder Systems International AB, all rights reserved.
import React, { useState } from 'react';

import { buttonStyle } from '../../../styles/constants';
import type { Beacon, BeaconViewProps } from '../../../types';
import { beaconAPI } from '../../../utils/apiClient';
import { formatRelativeTime } from '../../../utils/dateUtils';
import { useWarnings } from '../../../utils/hooks';
import logger from '../../../utils/logger';
import { WARNING_TYPE_LABELS } from '../../../utils/maintenanceUtils';
import { Button } from '../../common/Button';

import { ReplaceDeviceModal } from './ReplaceDeviceModal';

/** Get color for temperature value */
const getTempColor = (temp: number | null | undefined): string => {
  if (temp == null) return '#999';
  if (temp > 50 || temp < -5) return '#f44336';
  if (temp > 35 || temp < 10) return '#ff9800';
  return '#4caf50';
};

/** Get color for humidity value */
const getHumidityColor = (hum: number | null | undefined): string => {
  if (hum == null) return '#999';
  if (hum > 85) return '#f44336';
  if (hum > 70 || hum < 20) return '#ff9800';
  return '#4caf50';
};

/**
 * Returns true when a specific config field has been sent to the device
 * but not yet confirmed via port-32 echo.
 * Field names match the keys in pending_config: 'mac' | 'channel' | 'power' | 'datarate'
 */
const isConfigPending = (
  beacon: Beacon,
  field: 'mac' | 'channel' | 'power' | 'datarate'
): boolean => {
  if (!beacon.pending_config || !(field in beacon.pending_config)) return false;
  if (!beacon.pending_config_at) return false;
  if (!beacon.confirmed_config_at) return true;
  return new Date(beacon.pending_config_at) > new Date(beacon.confirmed_config_at);
};

/** Inline badge shown next to a config value while waiting for device confirmation. */
const PendingBadge: React.FC = () => (
  <span
    style={{
      marginLeft: '0.5vmin',
      fontSize: '1.1vmin',
      background: '#fff3cd',
      color: '#856404',
      border: '1px solid #ffc107',
      borderRadius: '0.3vmin',
      padding: '0 0.4vmin',
      fontWeight: 600,
    }}
    title="Sent to device — waiting for confirmation"
  >
    Pending
  </span>
);

/** Get color for signal strength value */
const getSignalColor = (rssi: number | null | undefined): string => {
  if (rssi == null) return '#999';
  if (rssi < -100) return '#f44336';
  if (rssi < -85) return '#ff9800';
  return '#4caf50';
};

/** Format number to 1 decimal */
const fmt1 = (v: number | null | undefined): string => (v != null ? v.toFixed(1) : '-');

interface BeaconStatus {
  status: 'critical' | 'off' | 'warning' | 'ok';
  icon: string;
  label: string;
  bgColor: string;
  borderColor: string;
}

interface DeckSummary {
  icon: string;
  text: string;
}

/** Geofence mode options shared by beacon and check-in station edit forms. */
const GEOFENCE_MODES = [
  { value: 0, label: '0 — Disabled' },
  { value: 1, label: '1 — Always overwrite' },
  { value: 2, label: '2 — Fallback only (solver > 200 m)' },
  { value: 3, label: '3 — Hybrid: geofence lat/lng + solver floor' },
  { value: 4, label: '4 — Hybrid: solver lat/lng + geofence floor' },
  { value: 5, label: '5 — Floor interpolation' },
] as const;

interface BeaconUpdates {
  deck?: number;
  side?: string;
  channel?: number | null;
  mac_address?: string | null;
  frame_number?: number | null;
  output_power?: number | null;
  datarate?: number | null;
  geofence?: number;
  geofence_threshold?: number | null;
  geofence_area?: string | null;
  geofence_lat?: number | null;
  geofence_lng?: number | null;
}

/**
 * BeaconView Component - LF Beacon management and monitoring
 *
 * Displays LF Beacons grouped by deck with:
 * - Status indicators (on/off, signal strength)
 * - Warnings and alarms
 * - Bulk operations per deck
 * - Detailed popup view for individual beacons
 *
 * @example
 * <BeaconView
 *   beacons={beacons}
 *   onToggleBeacon={handleToggleBeacon}
 *   onToggleDeck={handleToggleDeck}
 *   onUpdateBeacon={handleUpdateBeacon}
 *   onRefresh={fetchBeacons}
 * />
 */
export const BeaconView: React.FC<BeaconViewProps> = ({
  beacons,
  onToggleBeacon,
  onToggleDeck,
  onRefresh,
  isSuperAdmin,
}) => {
  const [selectedBeacon, setSelectedBeacon] = useState<Beacon | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [replaceMode, setReplaceMode] = useState<boolean>(false);
  const [editedDeck, setEditedDeck] = useState<string>('');
  const [editedSide, setEditedSide] = useState<string>('');
  const [editedChannel, setEditedChannel] = useState<string>('');
  const [editedMacAddress, setEditedMacAddress] = useState<string>('');
  const [editedFrameNumber, setEditedFrameNumber] = useState<string>('');
  const [editedOutputPower, setEditedOutputPower] = useState<string>('');
  const [editedDatarate, setEditedDatarate] = useState<string>('');
  const [editedGeofence, setEditedGeofence] = useState<number>(0);
  const [editedGeofenceThreshold, setEditedGeofenceThreshold] = useState<string>('');
  const [editedGeofenceArea, setEditedGeofenceArea] = useState<string>('');
  const [editedGeofenceLat, setEditedGeofenceLat] = useState<string>('');
  const [editedGeofenceLng, setEditedGeofenceLng] = useState<string>('');
  const [collapsedDecks, setCollapsedDecks] = useState<Set<string | number>>(new Set());

  // Use shared warnings hook
  const { warnings: activeWarnings, getWarningsForDevice } = useWarnings({ category: 'lf_beacon' });

  /**
   * Toggle deck collapse/expand
   */
  const toggleDeckCollapse = (deckNumber: string | number): void => {
    setCollapsedDecks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(deckNumber)) {
        newSet.delete(deckNumber);
      } else {
        newSet.add(deckNumber);
      }
      return newSet;
    });
  };

  /**
   * Handle beacon update
   */
  const handleUpdateBeacon = async (beaconId: string, updates: BeaconUpdates): Promise<void> => {
    try {
      const data = await beaconAPI.update(beaconId, updates);
      if (data.success) {
        logger.debug(`Beacon ${beaconId} updated`);
        setEditMode(false);
        setSelectedBeacon(null);
        // Refresh beacon list
        if (onRefresh) onRefresh();
      } else {
        alert(`Failed to update beacon: ${data.error}`);
      }
    } catch (err) {
      console.error('Error updating beacon:', err);
      alert(`Failed to update beacon: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  /**
   * Open edit mode for selected beacon
   */
  const openEditMode = (beacon: Beacon): void => {
    setEditMode(true);
    setEditedDeck(beacon.deck !== null ? beacon.deck.toString() : '0');
    setEditedSide(beacon.side || 'Unknown');
    setEditedChannel(
      beacon.channel !== null && beacon.channel !== undefined ? beacon.channel.toString() : ''
    );
    setEditedMacAddress(beacon.mac_address || '');
    setEditedFrameNumber(beacon.frame_number !== null ? beacon.frame_number.toString() : '');
    setEditedOutputPower(
      beacon.output_power !== null && beacon.output_power !== undefined
        ? beacon.output_power.toString()
        : ''
    );
    setEditedDatarate(
      beacon.datarate !== null && beacon.datarate !== undefined ? beacon.datarate.toString() : ''
    );
    setEditedGeofence(beacon.geofence ?? 0);
    setEditedGeofenceThreshold(
      beacon.geofence_threshold != null ? beacon.geofence_threshold.toString() : ''
    );
    setEditedGeofenceArea(beacon.geofence_area || '');
    setEditedGeofenceLat(beacon.geofence_lat != null ? beacon.geofence_lat.toString() : '');
    setEditedGeofenceLng(beacon.geofence_lng != null ? beacon.geofence_lng.toString() : '');
  };

  /**
   * Save beacon edits
   */
  const saveBeaconEdit = (): void => {
    if (!selectedBeacon) return;

    const updates: BeaconUpdates = {};
    if (editedDeck !== (selectedBeacon.deck !== null ? selectedBeacon.deck.toString() : '0')) {
      updates.deck = parseInt(editedDeck);
    }
    if (editedSide !== selectedBeacon.side) {
      updates.side = editedSide;
    }
    if (
      editedChannel !==
      (selectedBeacon.channel !== null && selectedBeacon.channel !== undefined
        ? selectedBeacon.channel.toString()
        : '')
    ) {
      updates.channel = editedChannel ? parseInt(editedChannel) : null;
    }
    if (editedMacAddress !== (selectedBeacon.mac_address || '')) {
      updates.mac_address = editedMacAddress || null;
    }
    if (
      editedFrameNumber !==
      (selectedBeacon.frame_number !== null ? selectedBeacon.frame_number.toString() : '')
    ) {
      updates.frame_number = editedFrameNumber ? parseInt(editedFrameNumber) : null;
    }
    const currentOutputPower =
      selectedBeacon.output_power !== null && selectedBeacon.output_power !== undefined
        ? selectedBeacon.output_power.toString()
        : '';
    if (editedOutputPower !== currentOutputPower) {
      updates.output_power = editedOutputPower ? parseInt(editedOutputPower) : null;
    }
    const currentDatarate =
      selectedBeacon.datarate !== null && selectedBeacon.datarate !== undefined
        ? selectedBeacon.datarate.toString()
        : '';
    if (editedDatarate !== currentDatarate) {
      updates.datarate = editedDatarate ? parseInt(editedDatarate) : null;
    }
    if (editedGeofence !== (selectedBeacon.geofence ?? 0)) {
      updates.geofence = editedGeofence;
    }
    const currentGeofenceThreshold =
      selectedBeacon.geofence_threshold != null ? selectedBeacon.geofence_threshold.toString() : '';
    if (editedGeofenceThreshold !== currentGeofenceThreshold) {
      updates.geofence_threshold = editedGeofenceThreshold
        ? parseInt(editedGeofenceThreshold)
        : null;
    }
    if (editedGeofenceArea !== (selectedBeacon.geofence_area || '')) {
      updates.geofence_area = editedGeofenceArea || null;
    }
    const currentGeofenceLat =
      selectedBeacon.geofence_lat != null ? selectedBeacon.geofence_lat.toString() : '';
    if (editedGeofenceLat !== currentGeofenceLat) {
      updates.geofence_lat = editedGeofenceLat ? parseFloat(editedGeofenceLat) : null;
    }
    const currentGeofenceLng =
      selectedBeacon.geofence_lng != null ? selectedBeacon.geofence_lng.toString() : '';
    if (editedGeofenceLng !== currentGeofenceLng) {
      updates.geofence_lng = editedGeofenceLng ? parseFloat(editedGeofenceLng) : null;
    }

    if (Object.keys(updates).length > 0) {
      handleUpdateBeacon(selectedBeacon.id, updates);
    }
    setEditMode(false);
    setSelectedBeacon(null);
  };

  /**
   * Handle device replacement completion
   */
  const handleDeviceReplacement = (): void => {
    setReplaceMode(false);
    setSelectedBeacon(null);
    // Refresh data to show updated devices
    if (onRefresh) onRefresh();
  };

  // Group beacons by deck
  const beaconsByDeck: Record<string | number, Beacon[]> = beacons.reduce(
    (acc, beacon) => {
      const deck = beacon.deck || 'Unknown';
      if (!acc[deck]) acc[deck] = [];
      acc[deck].push(beacon);
      return acc;
    },
    {} as Record<string | number, Beacon[]>
  );

  // Sort decks numerically (descending, with Unknown at bottom)
  const sortedDecks = Object.keys(beaconsByDeck).sort((a, b) => {
    if (a === 'Unknown') return 1;
    if (b === 'Unknown') return -1;
    return parseInt(b) - parseInt(a); // Descending order (high to low)
  });

  /**
   * Returns true if a downlink has been confirmed by the device and the confirmed
   * state differs from the expected state — i.e. we're waiting for the device to
   * apply a command that was sent but not yet echoed back via uplink.
   *
   * confirmed_state = null/undefined means no uplink received yet → not pending.
   */
  const isPending = (beacon: Beacon): boolean => {
    if (!beacon.confirmed_state) return false;
    return beacon.expected_state !== beacon.confirmed_state;
  };

  /**
   * Determine beacon status and styling based on expected_state and responsiveness.
   *
   * Priority: expected_state drives display, last_seen (1h threshold) indicates health.
   * - OFF / Pending off: expected_state is not 'on'
   * - NOT RESPONDING: expected ON but not seen within 1 hour
   * - ON (warning) / Pending on: responding but an active signal_weak warning is present
   * - ON (ok) / Pending on: responding with good signal
   *
   * Weak signal is driven by the backend warning system (DR-based, see
   * background-maintenance.js), not by RSSI — RSSI is not a reliable proxy for
   * sustained link quality since ADR is always active.
   *
   * Label shows "Pending on/off" when expected_state !== confirmed_state (and
   * confirmed_state is not null). Color stays based on confirmed/actual state.
   *
   * @param {Beacon} beacon - Beacon object with expected_state, confirmed_state, last_seen
   * @returns {BeaconStatus} Status object with icon, label, colors for rendering
   */
  const getBeaconStatus = (beacon: Beacon): BeaconStatus => {
    const now = Date.now();
    const lastSeen = beacon.last_seen ? new Date(beacon.last_seen).getTime() : 0;
    const ageHours = (now - lastSeen) / (1000 * 60 * 60);

    const isResponding = ageHours < 1; // Beacon seen within last hour
    const expectedOn = beacon.expected_state === 'on';
    const hasWeakSignalWarning = getWarningsForDevice(beacon.device_id).some(
      (w) => w.warning_type === 'signal_weak'
    );
    const pending = isPending(beacon);

    // Pending state — color reflects confirmed (actual) state, label signals what's in progress
    if (pending) {
      if (beacon.confirmed_state === 'on') {
        // Device is ON, command to turn OFF is in progress
        return {
          status: 'ok',
          icon: '🟢',
          label: 'Pending OFF',
          bgColor: 'white',
          borderColor: '#4CAF50',
        };
      } else {
        // Device is OFF, command to turn ON is in progress
        return {
          status: 'off',
          icon: '⚪',
          label: 'Pending ON',
          bgColor: '#f5f5f5',
          borderColor: '#999',
        };
      }
    }

    // Expected OFF
    if (!expectedOn) {
      return {
        status: 'off',
        icon: '⚪',
        label: 'OFF',
        bgColor: '#f5f5f5',
        borderColor: '#999',
      };
    }

    // Expected ON but not responding
    if (!isResponding) {
      return {
        status: 'critical',
        icon: '🔴',
        label: 'NOT RESPONDING',
        bgColor: '#ffebee',
        borderColor: '#f44',
      };
    }

    // Expected ON, responding, but weak signal per the backend's DR-based warning
    if (hasWeakSignalWarning) {
      return {
        status: 'warning',
        icon: '🟡',
        label: 'ON',
        bgColor: '#fff3e0',
        borderColor: '#ff9800',
      };
    }

    // Expected ON, responding, good signal
    return {
      status: 'ok',
      icon: '🟢',
      label: 'ON',
      bgColor: 'white',
      borderColor: '#4CAF50',
    };
  };

  /**
   * Get deck summary status
   */
  const getDeckSummary = (deckBeacons: Beacon[]): DeckSummary => {
    let critical = 0;
    let warningsCount = 0;

    deckBeacons.forEach((beacon) => {
      const status = getBeaconStatus(beacon);
      if (status.status === 'critical') critical++;
      if (status.status === 'warning') warningsCount++;
    });

    if (critical > 0)
      return { icon: '🔴', text: `${critical} critical alarm${critical > 1 ? 's' : ''}` };
    if (warningsCount > 0)
      return { icon: '⚠️', text: `${warningsCount} warning${warningsCount > 1 ? 's' : ''}` };
    return { icon: '✓', text: 'No issues' };
  };

  /**
   * Get overall statistics
   */
  const getOverallStats = () => {
    let totalOn = 0;
    let totalOff = 0;
    let critical = 0;

    beacons.forEach((beacon) => {
      const status = getBeaconStatus(beacon);
      if (status.status === 'ok' || status.status === 'warning') totalOn++;
      if (status.status === 'off') totalOff++;
      if (status.status === 'critical') critical++;
    });

    return { total: beacons.length, totalOn, totalOff, critical };
  };

  const stats = getOverallStats();

  return (
    <div>
      {/* Overall statistics */}
      <div
        style={{
          background: '#f9f9f9',
          padding: '1.5vmin',
          borderRadius: '0.5vmin',
          marginBottom: '2vmin',
          display: 'flex',
          gap: '2vmin',
          fontSize: '1.8vmin',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <strong>📊 Total:</strong> {stats.total} beacons
        </div>
        <div>
          <strong>✓ ON:</strong> {stats.totalOn}
        </div>
        <div>
          <strong>⚪ OFF:</strong> {stats.totalOff}
        </div>
        {stats.critical > 0 && (
          <div style={{ color: '#f44' }}>
            <strong>🔴 Critical:</strong> {stats.critical}
          </div>
        )}
      </div>

      {/* Active warnings banner */}
      {activeWarnings.length > 0 && (
        <div
          style={{
            marginBottom: '2vmin',
            borderRadius: '0.5vmin',
            border: `2px solid ${activeWarnings.some((w) => w.severity === 'critical') ? '#f44336' : '#ff9800'}`,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              background: activeWarnings.some((w) => w.severity === 'critical')
                ? '#f44336'
                : '#ff9800',
              color: 'white',
              padding: '0.8vmin 1.5vmin',
              fontSize: '1.6vmin',
              fontWeight: 'bold',
            }}
          >
            {activeWarnings.some((w) => w.severity === 'critical') ? '🔴' : '⚠️'} Active Warnings (
            {activeWarnings.length})
          </div>
          <div style={{ background: '#fffdf9', padding: '1vmin' }}>
            {activeWarnings.map((warning) => {
              const beacon = beacons.find((b) => b.device_id === warning.device);
              const isCritical = warning.severity === 'critical';
              return (
                <div
                  key={warning.id}
                  onClick={() => beacon && setSelectedBeacon(beacon)}
                  style={{
                    fontSize: '1.5vmin',
                    padding: '0.8vmin 1vmin',
                    marginBottom: '0.5vmin',
                    background: isCritical ? '#ffebee' : '#fff8e1',
                    borderLeft: `4px solid ${isCritical ? '#f44336' : '#ff9800'}`,
                    borderRadius: '0.3vmin',
                    cursor: beacon ? 'pointer' : 'default',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1vmin',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      background: isCritical ? '#f44336' : '#ff9800',
                      color: 'white',
                      fontSize: '1.1vmin',
                      fontWeight: 'bold',
                      padding: '0.2vmin 0.6vmin',
                      borderRadius: '1vmin',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isCritical ? '🔴 Critical' : '⚠ Warning'}
                  </span>
                  <span style={{ flex: 1 }}>
                    <strong>{beacon?.name ?? warning.device}</strong>
                    {' — '}
                    {WARNING_TYPE_LABELS[warning.warning_type] || warning.warning_type}
                  </span>
                  <span style={{ fontSize: '1.3vmin', color: '#888', whiteSpace: 'nowrap' }}>
                    {new Date(warning.detected_at).toLocaleString()}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Beacons grouped by deck */}
      {sortedDecks.map((deckNumber) => {
        const deckBeacons = beaconsByDeck[deckNumber];
        const summary = getDeckSummary(deckBeacons);
        const activeBeacons = deckBeacons.filter((b) => getBeaconStatus(b).status !== 'off');

        return (
          <div
            key={deckNumber}
            style={{
              marginBottom: '2vmin',
              background: 'white',
              borderRadius: '1vmin',
              border: '1px solid #ddd',
              overflow: 'hidden',
            }}
          >
            {/* Deck header */}
            <div
              style={{
                background: '#f5f5f5',
                padding: '1.5vmin',
                borderBottom: '1px solid #ddd',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1vmin',
              }}
            >
              <div
                onClick={() => toggleDeckCollapse(deckNumber)}
                style={{
                  fontSize: '2vmin',
                  fontWeight: 'bold',
                  flex: 1,
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1vmin',
                }}
              >
                <span style={{ fontSize: '1.5vmin' }}>
                  {collapsedDecks.has(deckNumber) ? '▶' : '▼'}
                </span>
                📊 Deck {deckNumber} {summary.icon} {summary.text}
              </div>
              {/* Only show toggle buttons for actual decks, not for "Unknown" */}
              {deckNumber !== 'Unknown' && (
                <div style={{ display: 'flex', gap: '0.5vmin' }}>
                  <Button
                    text="Turn off all"
                    onClick={() => onToggleDeck(deckNumber, 'off')}
                    disabled={activeBeacons.length === 0}
                    style={{
                      ...buttonStyle,
                      background: activeBeacons.length === 0 ? '#ccc' : '#ff9800',
                      color: 'white',
                      fontSize: '1.4vmin',
                      padding: '0.5vmin 1vmin',
                      opacity: activeBeacons.length === 0 ? 0.5 : 1,
                      cursor: activeBeacons.length === 0 ? 'not-allowed' : 'pointer',
                    }}
                  />
                  <Button
                    text="Turn on all"
                    onClick={() => onToggleDeck(deckNumber, 'on')}
                    disabled={deckBeacons.length === activeBeacons.length}
                    style={{
                      ...buttonStyle,
                      background: deckBeacons.length === activeBeacons.length ? '#ccc' : '#4CAF50',
                      color: 'white',
                      fontSize: '1.4vmin',
                      padding: '0.5vmin 1vmin',
                      opacity: deckBeacons.length === activeBeacons.length ? 0.5 : 1,
                      cursor:
                        deckBeacons.length === activeBeacons.length ? 'not-allowed' : 'pointer',
                    }}
                  />
                </div>
              )}
            </div>

            {/* Beacon list */}
            {!collapsedDecks.has(deckNumber) && (
              <div style={{ padding: '1vmin' }}>
                {deckBeacons.map((beacon) => {
                  const status = getBeaconStatus(beacon);

                  return (
                    <div
                      key={beacon.id}
                      onClick={() => setSelectedBeacon(beacon)}
                      style={{
                        padding: '1vmin',
                        marginBottom: '0.5vmin',
                        background: status.bgColor,
                        borderLeft: `4px solid ${status.borderColor}`,
                        borderRadius: '0.3vmin',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '1.6vmin',
                        transition: 'transform 0.1s',
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        {status.icon}{' '}
                        <strong>{beacon.name || `Beacon ${deckNumber}-${beacon.side}`}</strong>
                      </div>
                      <div style={{ minWidth: '150px', textAlign: 'center' }}>{status.label}</div>
                      <div style={{ minWidth: '100px', textAlign: 'center' }}>
                        {beacon.signal_strength ? `${beacon.signal_strength} dBm` : '-'}
                      </div>
                      <div
                        style={{
                          minWidth: '100px',
                          textAlign: 'right',
                          color: '#666',
                          fontSize: '1.4vmin',
                        }}
                      >
                        {formatRelativeTime(beacon.last_seen)}
                      </div>
                      <div style={{ minWidth: '120px', textAlign: 'right' }}>
                        {status.status === 'warning' && (
                          <span
                            style={{
                              display: 'inline-block',
                              background: '#ff9800',
                              color: 'white',
                              fontSize: '1.2vmin',
                              fontWeight: 'bold',
                              padding: '0.2vmin 0.6vmin',
                              borderRadius: '1vmin',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            ⚠ Weak signal
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Beacon detail popup */}
      {selectedBeacon && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            // flex-start + paddingTop instead of center: prevents modal from extending below the visible
            // viewport on iOS where 100vh includes browser chrome behind the address/tab bar
            alignItems: 'flex-start',
            paddingTop: 'max(20px, 5vh)',
            zIndex: 3000,
          }}
          onClick={() => {
            setEditMode(false);
            setSelectedBeacon(null);
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '1vmin',
              maxWidth: '500px',
              width: '90%',
              // dvh (dynamic viewport height) excludes browser chrome — ensures modal fits visible area on iOS
              maxHeight: 'min(90vh, 85dvh)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                overflowY: 'scroll',
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
                flex: 1,
                padding: '2vmin',
                overscrollBehavior: 'contain',
              }}
            >
              {/* Header */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '2vmin',
                  borderBottom: '2px solid #ddd',
                  paddingBottom: '1vmin',
                }}
              >
                <h2 style={{ margin: 0, fontSize: '2.5vmin' }}>
                  {editMode
                    ? 'Edit Beacon'
                    : selectedBeacon.name || `Beacon ${selectedBeacon.deck}-${selectedBeacon.side}`}
                </h2>
                <Button
                  text="✕"
                  onClick={() => {
                    setEditMode(false);
                    setSelectedBeacon(null);
                  }}
                  style={{
                    background: '#f44',
                    color: 'white',
                    fontSize: '2vmin',
                    padding: '0.5vmin 1vmin',
                  }}
                />
              </div>

              {/* Details or Edit Form */}
              {editMode ? (
                <div style={{ fontSize: '1.8vmin' }}>
                  {(() => {
                    const inputStyle: React.CSSProperties = {
                      width: '100%',
                      padding: '0.8vmin',
                      fontSize: '1.6vmin',
                      border: '1px solid #ccc',
                      borderRadius: '0.4vmin',
                      boxSizing: 'border-box',
                    };
                    const labelStyle: React.CSSProperties = {
                      display: 'block',
                      marginBottom: '0.3vmin',
                      fontWeight: 'bold',
                      fontSize: '1.5vmin',
                    };
                    const fieldStyle: React.CSSProperties = { marginBottom: '1vmin' };
                    const grid2: React.CSSProperties = {
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '1vmin',
                    };
                    const sectionHeader: React.CSSProperties = {
                      borderTop: '1px solid #ddd',
                      marginTop: '1vmin',
                      paddingTop: '1vmin',
                      marginBottom: '1vmin',
                      fontWeight: 'bold',
                      fontSize: '1.5vmin',
                      color: '#555',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    };
                    return (
                      <>
                        <div style={grid2}>
                          <div style={fieldStyle}>
                            <label style={labelStyle}>Deck</label>
                            <input
                              type="number"
                              value={editedDeck}
                              onChange={(e) => setEditedDeck(e.target.value)}
                              style={inputStyle}
                            />
                          </div>
                          <div style={fieldStyle}>
                            <label style={labelStyle}>Side</label>
                            <input
                              type="text"
                              value={editedSide}
                              onChange={(e) => setEditedSide(e.target.value)}
                              placeholder="Port / Starboard"
                              style={inputStyle}
                            />
                          </div>
                          <div style={fieldStyle}>
                            <label style={labelStyle}>Frame No.</label>
                            <input
                              type="number"
                              value={editedFrameNumber}
                              onChange={(e) => setEditedFrameNumber(e.target.value)}
                              placeholder="Optional"
                              style={inputStyle}
                            />
                          </div>
                          <div style={fieldStyle}>
                            <label style={labelStyle}>Channel</label>
                            <input
                              type="number"
                              value={editedChannel}
                              onChange={(e) => setEditedChannel(e.target.value)}
                              placeholder="Optional"
                              style={inputStyle}
                            />
                          </div>
                        </div>
                        <div style={fieldStyle}>
                          <label style={labelStyle}>MAC Address</label>
                          <input
                            type="text"
                            value={editedMacAddress}
                            onChange={(e) => setEditedMacAddress(e.target.value)}
                            placeholder="Optional"
                            style={inputStyle}
                          />
                        </div>
                        <div style={grid2}>
                          <div style={fieldStyle}>
                            <label style={labelStyle}>Output Power (dB)</label>
                            <input
                              type="number"
                              value={editedOutputPower}
                              onChange={(e) => setEditedOutputPower(e.target.value)}
                              placeholder="2-20"
                              min="2"
                              max="20"
                              style={inputStyle}
                            />
                          </div>
                          <div style={fieldStyle}>
                            <label style={labelStyle}>Datarate:</label>
                            <input
                              type="number"
                              value={editedDatarate}
                              onChange={(e) => setEditedDatarate(e.target.value)}
                              placeholder="-7 to 54"
                              min="-7"
                              max="54"
                              style={inputStyle}
                            />
                          </div>
                          <div style={fieldStyle}>
                            <label style={labelStyle}>Geofence Mode</label>
                            <select
                              value={editedGeofence}
                              onChange={(e) => setEditedGeofence(parseInt(e.target.value))}
                              style={inputStyle}
                            >
                              {GEOFENCE_MODES.map((m) => (
                                <option key={m.value} value={m.value}>
                                  {m.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div style={fieldStyle}>
                            <label style={labelStyle}>Geofence Threshold (dBm)</label>
                            <input
                              type="number"
                              value={editedGeofenceThreshold}
                              onChange={(e) => setEditedGeofenceThreshold(e.target.value)}
                              placeholder="Tomt = ingen filtrering"
                              style={inputStyle}
                            />
                          </div>
                          <div style={fieldStyle}>
                            <label style={labelStyle}>Geofence Area</label>
                            <input
                              type="text"
                              value={editedGeofenceArea}
                              onChange={(e) => setEditedGeofenceArea(e.target.value)}
                              placeholder="t.ex. Brygga, Däck 3"
                              style={inputStyle}
                            />
                          </div>
                        </div>
                        <div style={sectionHeader}>📍 Geofence Position</div>
                        <div style={grid2}>
                          <div style={fieldStyle}>
                            <label style={labelStyle}>Geofence Lat</label>
                            <input
                              type="number"
                              value={editedGeofenceLat}
                              onChange={(e) => setEditedGeofenceLat(e.target.value)}
                              placeholder="57.6789"
                              step="any"
                              style={inputStyle}
                            />
                          </div>
                          <div style={fieldStyle}>
                            <label style={labelStyle}>Geofence Lng</label>
                            <input
                              type="number"
                              value={editedGeofenceLng}
                              onChange={(e) => setEditedGeofenceLng(e.target.value)}
                              placeholder="11.9876"
                              step="any"
                              style={inputStyle}
                            />
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              ) : (
                <>
                  <div style={{ fontSize: '1.8vmin', lineHeight: '2.5' }}>
                    <div>
                      <strong>Status:</strong>{' '}
                      {(() => {
                        const s = getBeaconStatus(selectedBeacon);
                        return `${s.icon} ${s.label}`;
                      })()}
                    </div>
                    <div
                      style={{
                        background: '#f8f9fa',
                        borderRadius: '0.5vmin',
                        padding: '1vmin 1.5vmin',
                        margin: '0.5vmin 0',
                        textAlign: 'center',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '1.4vmin',
                          color: '#888',
                          marginBottom: '0.3vmin',
                          fontWeight: 600,
                        }}
                      >
                        Sensor
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: '2vmin',
                          fontSize: '1.8vmin',
                        }}
                      >
                        <span style={{ color: getTempColor(selectedBeacon.temperature) }}>
                          🌡️ {fmt1(selectedBeacon.temperature)}°C
                        </span>
                        <span style={{ color: getHumidityColor(selectedBeacon.humidity) }}>
                          💧 {fmt1(selectedBeacon.humidity)}%
                        </span>
                      </div>
                      <div style={{ fontSize: '1.8vmin', marginTop: '0.3vmin' }}>
                        <span style={{ color: getSignalColor(selectedBeacon.signal_strength) }}>
                          📶{' '}
                          {selectedBeacon.signal_strength
                            ? `${selectedBeacon.signal_strength} dBm`
                            : '-'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <strong>Deck:</strong>{' '}
                      {selectedBeacon.deck !== null ? selectedBeacon.deck : 'N/A'}
                    </div>
                    <div>
                      <strong>Side:</strong> {selectedBeacon.side || 'N/A'}
                    </div>
                    <div>
                      <strong>Frame No:</strong> {selectedBeacon.frame_number || 'N/A'}
                    </div>
                    {isSuperAdmin && (
                      <>
                        <div>
                          <strong>Channel:</strong> {selectedBeacon.channel || 'N/A'}
                          {isConfigPending(selectedBeacon, 'channel') && <PendingBadge />}
                        </div>
                        <div>
                          <strong>MAC Address:</strong> {selectedBeacon.mac_address || 'N/A'}
                          {isConfigPending(selectedBeacon, 'mac') && <PendingBadge />}
                        </div>
                        <div>
                          <strong>Output Power:</strong>{' '}
                          {selectedBeacon.output_power !== null &&
                          selectedBeacon.output_power !== undefined
                            ? `${selectedBeacon.output_power} dB`
                            : 'N/A'}
                          {isConfigPending(selectedBeacon, 'power') && <PendingBadge />}
                        </div>
                        <div>
                          <strong>Datarate:</strong>{' '}
                          {selectedBeacon.datarate !== null && selectedBeacon.datarate !== undefined
                            ? selectedBeacon.datarate
                            : 'N/A'}
                          {isConfigPending(selectedBeacon, 'datarate') && <PendingBadge />}
                        </div>
                        <div>
                          <strong>Geofence Mode:</strong>{' '}
                          {GEOFENCE_MODES.find((m) => m.value === (selectedBeacon.geofence ?? 0))
                            ?.label ?? 'N/A'}
                        </div>
                        <div>
                          <strong>Geofence Threshold:</strong>{' '}
                          {selectedBeacon.geofence_threshold != null
                            ? `${selectedBeacon.geofence_threshold} dBm`
                            : 'N/A'}
                        </div>
                        {selectedBeacon.geofence_area && (
                          <div>
                            <strong>Geofence Area:</strong> {selectedBeacon.geofence_area}
                          </div>
                        )}
                        {(selectedBeacon.geofence_lat != null ||
                          selectedBeacon.geofence_lng != null) && (
                          <div
                            style={{
                              borderTop: '1px solid #eee',
                              marginTop: '1vmin',
                              paddingTop: '1vmin',
                            }}
                          >
                            <strong>📍 Geofence Position:</strong>{' '}
                            {selectedBeacon.geofence_lat != null &&
                            selectedBeacon.geofence_lng != null
                              ? `${selectedBeacon.geofence_lat}, ${selectedBeacon.geofence_lng}`
                              : 'Not set'}
                          </div>
                        )}
                      </>
                    )}
                    <div>
                      <strong>Device ID:</strong> {selectedBeacon.device_id}
                    </div>
                    <div>
                      <strong>Last seen:</strong> {formatRelativeTime(selectedBeacon.last_seen)}
                    </div>
                    {selectedBeacon.last_state_change && (
                      <div
                        style={{
                          borderTop: '1px solid #eee',
                          marginTop: '1vmin',
                          paddingTop: '1vmin',
                          fontSize: '1.5vmin',
                          color: '#666',
                        }}
                      >
                        <div>
                          <strong>State changed:</strong>{' '}
                          {new Date(selectedBeacon.last_state_change).toLocaleString()}
                        </div>
                        {selectedBeacon.state_changed_by && (
                          <div>
                            <strong>Changed by:</strong> {selectedBeacon.state_changed_by}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Warnings from warning system */}
                  {(() => {
                    const deviceWarnings = getWarningsForDevice(selectedBeacon.device_id);
                    if (deviceWarnings.length > 0) {
                      return (
                        <div
                          style={{
                            marginTop: '2vmin',
                            padding: '1.5vmin',
                            background: '#fff3e0',
                            borderRadius: '0.5vmin',
                            border: '2px solid #ff9800',
                          }}
                        >
                          <div
                            style={{
                              fontSize: '1.8vmin',
                              fontWeight: 'bold',
                              marginBottom: '1vmin',
                            }}
                          >
                            ⚠️ Active Warnings ({deviceWarnings.length})
                          </div>
                          {deviceWarnings.map((warning, idx) => {
                            return (
                              <div
                                key={idx}
                                style={{
                                  fontSize: '1.5vmin',
                                  padding: '0.8vmin',
                                  marginBottom: '0.5vmin',
                                  background: warning.severity === 'critical' ? '#ffebee' : '#fff',
                                  borderLeft: `3px solid ${warning.severity === 'critical' ? '#f44' : '#ff9800'}`,
                                  borderRadius: '0.3vmin',
                                }}
                              >
                                <div style={{ fontWeight: 'bold' }}>
                                  {WARNING_TYPE_LABELS[warning.warning_type] ||
                                    warning.warning_type}
                                </div>
                                <div style={{ fontSize: '1.3vmin', color: '#666' }}>
                                  Detected: {new Date(warning.detected_at).toLocaleString()}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </>
              )}
            </div>
            {/* Control buttons — in footer outside scrollable area, always visible */}
            <div
              style={{
                flexShrink: 0,
                background: 'white',
                padding: '1.5vmin 2vmin',
                paddingBottom: '1vmin',
                borderTop: '1px solid #ddd',
                display: 'flex',
                gap: '1vmin',
                flexWrap: 'wrap',
              }}
            >
              {editMode ? (
                <>
                  <Button
                    text="✓ Save"
                    onClick={saveBeaconEdit}
                    style={{
                      flex: 1,
                      minWidth: '120px',
                      background: '#4CAF50',
                      color: 'white',
                      fontSize: '1.6vmin',
                      padding: '1vmin 1.5vmin',
                      border: 'none',
                      borderRadius: '0.5vmin',
                      boxSizing: 'border-box',
                    }}
                  />
                  <Button
                    text="Cancel"
                    onClick={() => setEditMode(false)}
                    style={{
                      flex: 1,
                      minWidth: '120px',
                      background: '#ccc',
                      color: '#333',
                      fontSize: '1.6vmin',
                      padding: '1vmin 1.5vmin',
                      border: 'none',
                      borderRadius: '0.5vmin',
                      boxSizing: 'border-box',
                    }}
                  />
                </>
              ) : (
                <>
                  <div
                    style={{
                      display: 'flex',
                      gap: '0.5vmin',
                      width: '100%',
                      marginBottom: '0.5vmin',
                    }}
                  >
                    {isSuperAdmin && (
                      <Button
                        text="✏️ Edit"
                        onClick={() => openEditMode(selectedBeacon)}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          background: '#2196F3',
                          color: 'white',
                          fontSize: '1.6vmin',
                          padding: '1vmin 1.5vmin',
                          border: 'none',
                          borderRadius: '0.5vmin',
                          boxSizing: 'border-box',
                        }}
                      />
                    )}
                    <Button
                      text="🔄 Replace"
                      onClick={() => setReplaceMode(true)}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        background: '#FF9800',
                        color: 'white',
                        fontSize: '1.6vmin',
                        padding: '1vmin 1.5vmin',
                        border: 'none',
                        borderRadius: '0.5vmin',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  {isSuperAdmin && (
                    <div style={{ display: 'flex', gap: '0.5vmin', width: '100%' }}>
                      <Button
                        text="Turn off"
                        onClick={() => {
                          onToggleBeacon(selectedBeacon.id, 'off');
                          setSelectedBeacon(null);
                        }}
                        disabled={selectedBeacon.expected_state === 'off'}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          background: selectedBeacon.expected_state === 'off' ? '#ccc' : '#ff9800',
                          color: 'white',
                          opacity: selectedBeacon.expected_state === 'off' ? 0.5 : 1,
                          cursor:
                            selectedBeacon.expected_state === 'off' ? 'not-allowed' : 'pointer',
                          fontSize: '1.6vmin',
                          padding: '1vmin 1.5vmin',
                          border: 'none',
                          borderRadius: '0.5vmin',
                          boxSizing: 'border-box',
                        }}
                      />
                      <Button
                        text="Turn on"
                        onClick={() => {
                          onToggleBeacon(selectedBeacon.id, 'on');
                          setSelectedBeacon(null);
                        }}
                        disabled={selectedBeacon.expected_state === 'on'}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          background: selectedBeacon.expected_state === 'on' ? '#ccc' : '#4CAF50',
                          color: 'white',
                          opacity: selectedBeacon.expected_state === 'on' ? 0.5 : 1,
                          cursor:
                            selectedBeacon.expected_state === 'on' ? 'not-allowed' : 'pointer',
                          fontSize: '1.6vmin',
                          padding: '1vmin 1.5vmin',
                          border: 'none',
                          borderRadius: '0.5vmin',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Replace device modal */}
      {replaceMode && selectedBeacon && (
        <ReplaceDeviceModal
          device={selectedBeacon}
          deviceCategory="lf_beacon"
          onReplace={handleDeviceReplacement}
          onCancel={() => setReplaceMode(false)}
        />
      )}
    </div>
  );
};

export default BeaconView;
